import React from 'react';
import Icon from '../../shared/Icon';
import './AlertSteps.css';
import {
  COMPARATORS,
  COMPARISON_SHAPES,
  PERIOD_UNITS,
  THRESHOLD_KINDS,
  columnLabel,
  confirmValuesLabel,
  offeredValuesLabel,
  describeComparison,
  describeRefusal,
  resultColumns,
  shapeUses,
} from '../alertSpec';

/**
 * The comparison half of step 2 — what the alert does with what the query
 * returned.
 *
 * **Every control here used to be a text input, and that is the defect this
 * component exists to fix.** PRD-1248 shipped five typed-identifier inputs: the
 * table the comparison reads, the column identifying the subject, the measure
 * column, the column ordering the periods, and a three-value subject grain. An
 * author had to know their physical schema to fill them in, nothing checked
 * what they typed against the query they had just previewed, and one of the
 * placeholders named a first-customer column outright.
 *
 * So the rule this component is built to is narrow and absolute: **no control
 * accepts a typed table or column name.** Every column below is chosen from the
 * result the author was just shown, which is what makes the alert portable
 * across tenants, datasets and verticals — the query names the data, and the
 * comparison names the query's output.
 *
 * The second rule is that no label, placeholder or helper text may name
 * first-customer vocabulary. There is no `e.g. member_id` here, and there is no
 * `e.g.` anywhere at all: an example is where domain words get in.
 */

/**
 * One result column, chosen rather than typed.
 *
 * Empty options when the query has not run yet, which is a real state on an
 * edit form — the stored document holds the comparison and the query but not
 * the columns the query returned, so an alert opens with its chosen columns
 * and no list until the preview refills it.
 *
 * The stored value is kept as an option in that case rather than silently
 * dropped. A `<select>` whose value is not among its options renders blank, and
 * a blank control that then saves its blank is exactly the silent change this
 * work is meant to remove.
 */
const ColumnSelect = ({ id, label, value, columns, onChange, helper, required = true }) => {
  const known = columns.some((c) => c.name === value);
  return (
    <div className="resui-field">
      <label className="resui-field-label" htmlFor={id}>
        {label}
        {required && <span className="resui-field-required">*</span>}
      </label>
      <select
        id={id}
        className="resui-select"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Choose a column from the result</option>
        {/* The stored choice, when the result cannot vouch for it yet. Labelled
            so the author can tell the two states apart. */}
        {value && !known && <option value={value}>{`${value} (from the saved alert)`}</option>}
        {columns.map((c) => (
          <option key={c.name} value={c.name}>{columnLabel(c)}</option>
        ))}
      </select>
      {helper && <span className="resui-field-helper">{helper}</span>}
    </div>
  );
};

const AlertThresholdFields = ({
  formData, onSetFormData, onConfirmValues, onUseValues, building = false,
}) => {
  const alert = formData.alert ?? {};
  const columns = resultColumns(alert.resultColumns);
  const shape = COMPARISON_SHAPES.find((s) => s.id === alert.shape);
  const refusal = describeRefusal(alert.authorRefusal);

  const setAlertField = (patch) => {
    onSetFormData((prev) => ({ ...prev, alert: { ...(prev.alert ?? {}), ...patch } }));
  };

  /**
   * Switching shape clears what the new shape cannot read.
   *
   * Not cosmetic: the API **refuses** a field the shape does not read rather
   * than ignoring it, so a run length left behind by a shape the author moved
   * away from would be refused on save, naming a control that is no longer on
   * screen. `buildAlertSpec` omits them too — this is the belt to that's
   * braces, and it also stops the review card describing a rule that is not in
   * force.
   */
  const setShape = (next) => {
    const patch = { shape: next };
    if (!shapeUses(next, 'measureColumn')) patch.measureColumn = '';
    if (!shapeUses(next, 'threshold')) {
      patch.threshold = '';
      patch.thresholdColumn = '';
      patch.thresholdKind = 'value';
    }
    if (!shapeUses(next, 'periodColumn')) patch.periodColumn = '';
    if (!shapeUses(next, 'periodUnit')) patch.periodUnit = '';
    if (!shapeUses(next, 'runLength')) patch.runLength = '';
    setAlertField(patch);
  };

  const comparisonSentence = describeComparison({
    shape: alert.shape,
    comparator: alert.comparator,
    threshold: alert.threshold,
    thresholdKind: alert.thresholdKind,
    thresholdColumn: alert.thresholdColumn,
    measureColumn: alert.measureColumn,
    // Without these the run-length half of the sentence never renders, so a run
    // would state its per-period rule and omit what makes it a run.
    periodUnit: alert.periodUnit,
    runLength: alert.runLength,
  });

  // The question has not been turned into a query yet, so there is nothing to
  // compare and no columns to choose from. Saying so once, here, is better than
  // rendering five empty pickers and a list of complaints under them.
  if (!alert.sql && !refusal) {
    return (
      <div className="alert-threshold-block">
        <h4 className="alert-threshold-heading">Comparison</h4>
        <div className="resui-banner resui-banner--info alert-inline-block">
          <Icon name="info" />
          <span className="resui-banner-text">
            Describe what to watch above and run it. The comparison is built from
            the columns the result comes back with.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="alert-threshold-block">
      <h4 className="alert-threshold-heading">Comparison</h4>

      {/*
        The refusal, in its own words rather than as a validation dump.

        Rendered here and not only at the query because the threshold control
        is immediately below, and a refusal an author can answer should be next
        to the thing that answers it. The classes that survive the vocabulary
        are about the question itself, so they say so and point back up — but
        the placement is what makes that legible rather than arbitrary.
      */}
      {refusal && (
        <div className="resui-banner resui-banner--alert alert-inline-block">
          <Icon name="warning_amber" />
          <div className="resui-banner-text">
            <strong>{refusal.title}</strong>
            <p>{refusal.body}</p>
            {/* Only on the author's click: a value nobody confirmed is still refused,
                so a typo never saves as an alert that cannot fire. */}
            {refusal.useValues && onUseValues && (
              <button
                type="button"
                className="resui-btn resui-btn--secondary"
                disabled={building}
                onClick={() => onUseValues(refusal.useValues.map(({ column, asked, used }) => ({ column, asked, used })))}
              >
                {offeredValuesLabel(refusal.useValues)}
              </button>
            )}
            {refusal.confirmValues && onConfirmValues && (
              <button
                type="button"
                className="resui-btn resui-btn--secondary"
                disabled={building}
                onClick={() => onConfirmValues(refusal.confirmValues)}
              >
                {confirmValuesLabel(refusal.confirmValues)}
              </button>
            )}
          </div>
        </div>
      )}

      {alert.sql && (
        <>
          <div className="resui-field">
            <label className="resui-field-label" htmlFor="alert-shape">
              What the alert does with the result <span className="resui-field-required">*</span>
            </label>
            <select
              id="alert-shape"
              className="resui-select"
              value={alert.shape ?? ''}
              onChange={(e) => setShape(e.target.value)}
            >
              <option value="">Select a comparison</option>
              {COMPARISON_SHAPES.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
            {shape && <span className="resui-field-helper">{shape.description}</span>}
          </div>

          <div className="alert-field-row">
            <ColumnSelect
              id="alert-subject-column"
              label="Column identifying the subject"
              value={alert.subjectColumn}
              columns={columns}
              onChange={(subjectColumn) => setAlertField({ subjectColumn })}
              helper="Each distinct value is one thing the alert can match."
            />

            {shapeUses(alert.shape, 'measureColumn') && (
              <ColumnSelect
                id="alert-measure-column"
                label="Column holding the value to compare"
                value={alert.measureColumn}
                columns={columns}
                onChange={(measureColumn) => setAlertField({ measureColumn })}
                helper="The number the query worked out for each subject."
              />
            )}
          </div>

          {shapeUses(alert.shape, 'comparator') && (
            <div className="alert-field-row">
              <div className="resui-field">
                <label className="resui-field-label" htmlFor="alert-comparator">
                  How it is compared <span className="resui-field-required">*</span>
                </label>
                <select
                  id="alert-comparator"
                  className="resui-select"
                  value={alert.comparator ?? '>='}
                  onChange={(e) => setAlertField({ comparator: e.target.value })}
                >
                  {COMPARATORS.map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
                {/*
                  **AC6, at the point of choice.** The comparators are
                  deliberately not uniform — some alerts fire on a value exactly
                  at the threshold and one is a strict `<` — so whether equality
                  fires is stated here rather than left to be inferred from the
                  symbol.
                */}
                <span className="resui-field-helper">
                  {COMPARATORS.find((c) => c.id === (alert.comparator ?? '>='))?.inclusive
                    ? 'A value sitting exactly on the threshold counts as breached.'
                    : 'A value sitting exactly on the threshold does not count as breached.'}
                </span>
              </div>

              <div className="resui-field">
                <label className="resui-field-label" htmlFor="alert-threshold-kind">
                  Compared against <span className="resui-field-required">*</span>
                </label>
                <select
                  id="alert-threshold-kind"
                  className="resui-select"
                  value={alert.thresholdKind ?? 'value'}
                  onChange={(e) => setAlertField({
                    thresholdKind: e.target.value,
                    // The other half is cleared, because the API refuses a
                    // comparison carrying both and a value left behind by the
                    // control the author switched away from is exactly how that
                    // happens.
                    ...(e.target.value === 'column' ? { threshold: '' } : { thresholdColumn: '' }),
                  })}
                >
                  {THRESHOLD_KINDS.map((k) => (
                    <option key={k.id} value={k.id}>{k.label}</option>
                  ))}
                </select>
                <span className="resui-field-helper">
                  A column compares each subject against its own value.
                </span>
              </div>

              {alert.thresholdKind === 'column' ? (
                <ColumnSelect
                  id="alert-threshold-column"
                  label="Column holding the threshold"
                  value={alert.thresholdColumn}
                  columns={columns}
                  onChange={(thresholdColumn) => setAlertField({ thresholdColumn })}
                />
              ) : (
                <div className="resui-field">
                  <label className="resui-field-label" htmlFor="alert-threshold">
                    Threshold<span className="resui-field-required">*</span>
                  </label>
                  <input
                    id="alert-threshold"
                    type="number"
                    className="resui-input"
                    value={alert.threshold ?? ''}
                    onChange={(e) => setAlertField({ threshold: e.target.value })}
                  />
                  <span className="resui-field-helper">
                    A blank threshold is not zero — the step will not continue without one.
                  </span>
                </div>
              )}
            </div>
          )}

          {shapeUses(alert.shape, 'runLength') && (
            <div className="alert-field-row">
              <ColumnSelect
                id="alert-period-column"
                label="Column that groups the result into periods"
                value={alert.periodColumn}
                columns={columns}
                onChange={(periodColumn) => setAlertField({ periodColumn })}
                helper="One row per subject per period."
              />

              <div className="resui-field">
                <label className="resui-field-label" htmlFor="alert-period-unit">
                  How long one period is<span className="resui-field-required">*</span>
                </label>
                <select
                  id="alert-period-unit"
                  className="resui-select"
                  value={alert.periodUnit ?? ''}
                  onChange={(e) => setAlertField({ periodUnit: e.target.value })}
                >
                  <option value="">Select a period</option>
                  {PERIOD_UNITS.map((u) => (
                    <option key={u.id} value={u.id}>{u.label}</option>
                  ))}
                </select>
                <span className="resui-field-helper">
                  Needed to tell a gap in the data from an unbroken run.
                </span>
              </div>

              <div className="resui-field">
                <label className="resui-field-label" htmlFor="alert-run-length">
                  Periods in a row<span className="resui-field-required">*</span>
                </label>
                <input
                  id="alert-run-length"
                  type="number"
                  min="2"
                  className="resui-input"
                  value={alert.runLength ?? ''}
                  onChange={(e) => setAlertField({ runLength: e.target.value })}
                />
                <span className="resui-field-helper">
                  At least 2 — a run of one is just a threshold breach.
                </span>
              </div>
            </div>
          )}

          {comparisonSentence && (
            <div className="resui-banner resui-banner--info alert-semantics">
              <Icon name="rule" />
              <span className="resui-banner-text">{comparisonSentence}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AlertThresholdFields;
